import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import type { Webhook, WebhookTrigger, CreateWebhookDto, SandboxConfig } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { CodeEditor } from './CodeEditor';
import { PolicySelect } from './PolicySelect';
import { TriggerConfigSection } from './TriggerConfigSection';

interface WebhookFormProps {
  /** Prepopulate the form when editing an existing webhook. */
  initial?: Webhook;
  submitLabel?: string;
  isSubmitting?: boolean;
  onCancel?: () => void;
  onSubmit: (payload: CreateWebhookDto & { status?: 'active' | 'disabled' }) => void;
}

function defaultTrigger(): WebhookTrigger {
  return { kind: 'http', methods: ['GET', 'POST'], secret: { headerName: 'x-webhook-secret', algo: 'sha256' } };
}

/**
 * Structural JS syntax check for sandbox handler code (F3).
 *
 * The webhook sandbox (@nyariv/sandboxjs 0.9.6) evaluates code as a SCRIPT
 * BODY — not an ES module. `new Function(src)` parses the source without
 * executing it and throws a SyntaxError for malformed input AND for module-only
 * syntax such as `export default function handler() {}`. This exactly matches
 * the sandbox contract: bare `handler(...) {...}` and `export default ...` are
 * rejected, while `$incoming.subscribe(...)` (and `emit(...)` at top level)
 * parse as valid AND execute successfully at runtime. NOTE: `handler = ...`
 * parses but is NOT a sandbox global — assigning to it throws a runtime
 * `handler is not defined` ReferenceError, so it must not be suggested as a
 * template. The body is never invoked here, so this is side-effect free.
 */
export function isValidJs(code: string): boolean {
  try {
    // eslint-disable-next-line no-new-func, @typescript-eslint/no-implied-eval
    new Function(code); // parse-only; never invoked.
    return true;
  } catch {
    return false;
  }
}

/** Error copy shown inline when handler code fails the sandbox syntax gate. */
export const INVALID_CODE_MESSAGE =
  'Handler code must be valid JavaScript. The sandbox runs a script body (no import/export) — use `$incoming.subscribe((event) => { ... })` or top-level `emit({...})`, not `export default ...`.';

/** Helper text shown under the editor, seeded with a sandbox-valid template. */
const CODE_HINT =
  'Write your sandbox handler as a script body. Example: `$incoming.subscribe((event) => { emit({ kind: "event", type: event?.type }); });`. ' +
  'The sandbox does not support import/export (no module syntax). ' +
  'Available globals: $incoming, $outcoming, emit, emitHttp, emitInternal, console.';

/**
 * Strip empty values from a sandbox config so the backend receives clean input.
 */
function cleanSandboxConfig(cfg: SandboxConfig): SandboxConfig | undefined {
  const out: SandboxConfig = {};
  if (cfg.timeoutMs != null && cfg.timeoutMs > 0) out.timeoutMs = cfg.timeoutMs;
  if (cfg.executionQuota) out.executionQuota = cfg.executionQuota;
  if (cfg.capEmits != null && cfg.capEmits > 0) out.capEmits = cfg.capEmits;
  if (cfg.consoleCaptureBytes != null && cfg.consoleCaptureBytes > 0)
    out.consoleCaptureBytes = cfg.consoleCaptureBytes;
  return Object.keys(out).length > 0 ? out : undefined;
}

export function WebhookForm({ initial, submitLabel = 'Save', isSubmitting, onCancel, onSubmit }: WebhookFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [trigger, setTrigger] = useState<WebhookTrigger>(initial?.trigger ?? defaultTrigger());
  const [code, setCode] = useState(initial?.code ?? '');
  const [policyIds, setPolicyIds] = useState<string[]>(initial?.policyIds ?? []);
  const [sandbox, setSandbox] = useState<SandboxConfig>(initial?.sandboxConfig ?? {});
  const [codeError, setCodeError] = useState<string | null>(null);

  // Reset the form when the target webhook changes (e.g. navigating between edits).
  useEffect(() => {
    setName(initial?.name ?? '');
    setDescription(initial?.description ?? '');
    setTrigger(initial?.trigger ?? defaultTrigger());
    setCode(initial?.code ?? '');
    setPolicyIds(initial?.policyIds ?? []);
    setSandbox(initial?.sandboxConfig ?? {});
  }, [initial?.id, initial?._id]);

  const setSandboxField = (key: keyof SandboxConfig, raw: string | undefined) => {
    let value: number | string | undefined;
    if (raw !== undefined && raw !== '') {
      if (key === 'executionQuota') value = raw;
      else value = Number(raw);
    }
    setSandbox((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    // F3: gate the submit on the sandbox syntax contract — never send invalid
    // JS to the backend (which now also rejects it with a 400).
    if (!isValidJs(code)) {
      setCodeError(INVALID_CODE_MESSAGE);
      return;
    }
    setCodeError(null);

    const dto: CreateWebhookDto = {
      name: name.trim(),
      description: description.trim() || undefined,
      triggerKind: trigger.kind,
      trigger,
      code,
      policyIds: policyIds.length > 0 ? policyIds : undefined,
      sandboxConfig: cleanSandboxConfig(sandbox),
    };
    onSubmit(dto);
  };

  return (
    <div className="space-y-6">
      {/* Basics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="webhook-name">
            Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="webhook-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Notify on new storage file"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="webhook-description">Description</Label>
          <Input
            id="webhook-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
        </div>
      </div>

      {/* Trigger */}
      <div className="space-y-2">
        <TriggerConfigSection trigger={trigger} onTriggerChange={setTrigger} />
      </div>

      {/* Code */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="webhook-code">
            Handler code <span className="text-destructive">*</span>
          </Label>
        </div>
        <div
          className={cn(
            'overflow-hidden rounded-md border',
            codeError ? 'border-destructive' : 'border-input',
          )}
        >
          <CodeEditor
            language="javascript"
            value={code}
            onChange={(next) => {
              setCode(next);
              setCodeError(null);
            }}
            height={360}
          />
        </div>
        {codeError && <p className="text-xs text-destructive">{codeError}</p>}
        <p className="text-xs text-muted-foreground">{CODE_HINT}</p>
      </div>

      {/* Policies */}
      <PolicySelect value={policyIds} onChange={setPolicyIds} />

      {/* Sandbox config */}
      <div className="space-y-2">
        <Label>Sandbox configuration</Label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Timeout (ms)</span>
            <Input
              type="number"
              value={sandbox.timeoutMs ?? ''}
              onChange={(e) => setSandboxField('timeoutMs', e.target.value)}
              placeholder="1000"
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Execution quota</span>
            <Input
              value={sandbox.executionQuota ?? ''}
              onChange={(e) => setSandboxField('executionQuota', e.target.value)}
              placeholder="e.g. 1mb / 100ms"
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Max emits</span>
            <Input
              type="number"
              value={sandbox.capEmits ?? ''}
              onChange={(e) => setSandboxField('capEmits', e.target.value)}
              placeholder="10"
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Console capture (bytes)</span>
            <Input
              type="number"
              value={sandbox.consoleCaptureBytes ?? ''}
              onChange={(e) => setSandboxField('consoleCaptureBytes', e.target.value)}
              placeholder="4096"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 border-t pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !name.trim() || !code.trim()}
        >
          <Save className="mr-1.5 h-4 w-4" />
          {isSubmitting ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </div>
  );
}